// CADOfferBanner
// --------------
// Soft-prompt banner offering CAD pricing to visitors whose browser locale
// is NOT *-CA but whose IANA timezone IS Canadian — i.e., probable Canadians
// using factory-default en-US browsers (the vast majority of Canadian
// devices).
//
// Behavior:
//   - Renders only when shouldOfferCAD() returns true AND user is on USD
//     AND user hasn't made a decision about the banner yet.
//   - "Use switch for CAD pricing" → calls setIsCADCurrency(true) AND
//     persists the dismissal so the banner never appears again.
//   - X icon → persists the dismissal. Banner never appears again.
//   - Once the user has made ANY decision (accept or dismiss), they will
//     not see this banner again on this device. This respects the user's
//     time and avoids nagging.
//
// US users never see this banner. The shouldOfferCAD() check requires a
// Canadian IANA timezone, which a US user cannot have without deliberate
// VPN or system tampering.

import React, { useMemo } from 'react'
import { Box, IconButton, Typography, Button } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useShoppingCart } from '../../context/ShoppingCartContext'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { Colors } from '../../styles/theme'
import { shouldOfferCAD } from '../../utilities/detectInitialCurrency'
import CAD from '../toggleCurrency/cad.svg'

// localStorage key for persisting the banner-decided state. Any truthy
// value means "user has made a decision; don't show this banner again."
const DECIDED_KEY = 'cad-offer-decided'

export default function CADOfferBanner() {
    const { isCAD, setIsCADCurrency } = useShoppingCart()

    // shouldOfferCAD() reads navigator + Intl, both of which are stable for
    // the duration of a tab. Memoizing avoids re-running the timezone check
    // on every parent re-render.
    const offerable = useMemo(() => shouldOfferCAD(), [])

    // Persistent dismissal — once true, the banner will never render again
    // on this device, regardless of currency state. This treats both
    // outcomes (accept CAD, dismiss banner) as a final decision the user
    // doesn't want re-prompted.
    const [decided, setDecided] = useLocalStorage(DECIDED_KEY, false)

    // Hide if: not a Canadian-timezone user, already on CAD (no point
    // offering what they already have), or user has made a decision before.
    if (!offerable || isCAD || decided) return null

    const handleSwitch = () => {
        setIsCADCurrency(true)
        setDecided(true)
    }

    const handleDismiss = () => {
        setDecided(true)
    }

    return (
        <Box
            role="region"
            aria-label="Currency suggestion"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 1, sm: 2 },
                pl: { xs: 1.5, sm: 2 },
                // Reserve space on the right for the absolute-positioned
                // X button so it never crowds the message/CTA. 56px =
                // X button width (44px) + comfortable inset (12px).
                pr: { xs: 7, sm: 8 },
                py: 0.75,
                // Subtle gold-tinted background — readable on the page's
                // cream/white but distinguishable as "this is a notice."
                backgroundColor: 'rgba(185, 134, 71, 0.10)',
                borderBottom: `1px solid rgba(185, 134, 71, 0.25)`,
                color: Colors.secondary,
                position: 'relative',
                // Slim profile so the banner doesn't dominate the viewport.
                minHeight: 44,
                // Allow the message + button to wrap on very narrow screens.
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    textAlign: 'center',
                    // Inline flex so the flag sits on the same baseline as
                    // the text without needing a separate wrapping rule.
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                Shipping to Canada?
                {/* Flag is desktop-only — mobile keeps text alone to
                    preserve breathing room next to the CTA button. */}
                <Box
                    component="img"
                    src={CAD}
                    alt=""
                    sx={{
                        display: { xs: 'none', sm: 'inline-block' },
                        width: 22,
                        height: 'auto',
                        borderRadius: 2,
                        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.12)',
                        verticalAlign: 'middle',
                        flexShrink: 0,
                    }}
                />
            </Typography>

            <Button
                onClick={handleSwitch}
                size="small"
                variant="outlined"
                sx={{
                    color: Colors.secondary,
                    borderColor: Colors.boltGold,
                    backgroundColor: 'transparent',
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                    textTransform: 'none',
                    letterSpacing: '0.01em',
                    px: 1.5,
                    py: 0.25,
                    minHeight: 32,
                    whiteSpace: 'nowrap',
                    transition:
                        'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 82, 71, 0.08)',
                        borderColor: Colors.greenLight,
                        color: Colors.greenLight,
                    },
                }}
            >
                Switch to CAD pricing
            </Button>

            <IconButton
                onClick={handleDismiss}
                aria-label="Dismiss currency suggestion"
                sx={{
                    color: Colors.secondary,
                    // Position absolute so the X never affects horizontal
                    // centering of the message + CTA. Banner reserves
                    // padding-right to keep the absolute X in its own zone.
                    position: 'absolute',
                    right: { xs: 6, sm: 10 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    // Comfortable 44px tap target (Apple HIG minimum).
                    width: 44,
                    height: 44,
                    borderRadius: 1.5,
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 82, 71, 0.08)',
                        color: Colors.greenLight,
                    },
                }}
            >
                <CloseIcon sx={{ fontSize: '1.4rem' }} />
            </IconButton>
        </Box>
    )
}
